import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { MdDeleteOutline, MdModeEditOutline } from "react-icons/md";
import { TbFileSearch } from "react-icons/tb";
import { Link } from "react-router-dom";
import { Dimmer, Grid, Loader, Placeholder, Table, TableRow } from "semantic-ui-react";
import { useDeleteProductById, useGetProductDataV2 } from "../../../api/product";
import PlusIcon from "../../../assets/commonIcon/PlusIcon";
import { CommonTable, CustomButton, InputText, TitleBar } from "../../../components";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import PaginationView from "../../../components/pagination/Pagination";
import { modelContentType, ProductTableCol } from "../../../config/constants";
import { MappingSourceType } from "../../../config/enums/MappingSourceType.enum";
import { CalculationFilterStatus } from "../../../config/enums/ProductType.enum";
import { ROLES } from "../../../config/permission-maps";
import { errorView } from "../../../helpers/ErrorHandler";
import { getUserRolesInLocal } from "../../../utils/cacheStorage";
import IngredientRegisterModalView from "../../ingredients/ingredientRegister/IngredientRegisterModalView";
import ProductDetailModal from "../productDetails/ProductDetailsModal";
import ProductEditModal from "../productEdit/ProductEditModal";
import ProductRegisterModal from "../productRegister/ProductRegisterModal";
import ProductFilter from "./ProductFilter";
import "./productList.scss";
interface SalesUnit {
  _id: string;
  sales_unit_name?: string;
  climate_impact?: string;
}

interface Product {
  _id?: string;
  product_name?: string;
  sales_units?: SalesUnit[];
  registration_number?: string;
}

const ProductList: React.FC = () => {
  const [productId, setProductId] = useState<string>("");
  const [visibleModal, setVisibleModal] = useState<boolean>(false);
  const [visibleProductDetailsModal, setVisibleProductDetailsModal] = useState<boolean>(false);
  const [visibleProductEditModal, setVisibleProductEditModal] = useState<boolean>(false);
  const [visibleDeleteModal, setVisibleDeleteModal] = useState<boolean>(false);
  const [deleteData, setDeleteData] = useState<Product | null>(null);
  const [visibleRegModal, setVisibleRegModal] = useState<boolean>(false);
  const [dataToPass, setDataToPass] = useState<any>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [calculateStatus, setCalculateStatus] = useState<CalculationFilterStatus>(
    CalculationFilterStatus.NONE
  );
  const [searchKey, setSearchKey] = useState("");
  const [orderBy, setOrderBy] = useState("createdAtDesc");

  const localUserRole = getUserRolesInLocal();
  const checkIsUserSupplier = localUserRole?.[0] === ROLES.supplier;

  const { register, watch } = useForm();
  const [searchProduct] = watch(["searchProduct"]);

  const {
    data,
    isLoading: productListLoading,
    refetch
  } = useGetProductDataV2({
    product_status: "ACTIVE",
    pageNumber,
    pageSize,
    calculateStatus,
    searchKey: searchProduct,
    orderBy
  });

  const refetchWithFilters = (status: CalculationFilterStatus, order: string) => {
    setCalculateStatus(status);
    setOrderBy(order);
  };

  const { isLoading, mutate } = useDeleteProductById();

  const totalPages = Math.ceil((data?.total || 0) / (data?.pageSize || 1));

  const getProductDetails = (id: string, type: number) => {
    setProductId(id);
    if (type === modelContentType.edit) {
      setVisibleProductEditModal(true);
    } else {
      setVisibleProductDetailsModal(true);
    }
  };

  const handleProductDeleteConfirmation = () => {
    if (deleteData?.sales_units?.length) {
      errorView("This Product has Sales Units, it cannot be deleted.");
      return;
    }
    deleteProductDetails(deleteData);
  };

  const deleteProductDetails = (product: any) => {
    mutate(product._id, {
      onSuccess: () => {
        refetch();
      }
    });
    setDeleteData(null);
  };

  const handleNavigationRegister = (status: any, data: any) => {
    if (status) {
      refetch();
      setVisibleModal(false);
      if (data?.enableNavigate || false) {
        setDataToPass(data.productData);
      }
      if (data?.enableEditNavigation || false) {
        setProductId(data.productData.product_id);
        setVisibleProductEditModal(true);
      }
      setVisibleRegModal(data?.enableNavigate || false);
    }
  };

  const loadTableData = () => {
    if (productListLoading) {
      return (
        <>
          {[...Array(5)].map((_, i) => (
            <TableRow key={i}>
              <Table.Cell colSpan={7}>
                <Placeholder>
                  <Placeholder.Line length="full" />
                  <Placeholder.Line length="very long" />
                </Placeholder>
              </Table.Cell>
            </TableRow>
          ))}
        </>
      );
    }
    {
      return data?.result?.map((product, index) => {
        const isAverageProduct = product?.is_average_product || false;
        return (
          <Table.Row className="tbleR" key={index}>
            <Table.Cell>
              <p>{product.product_name}</p>
            </Table.Cell>
            <Table.Cell>{renderSalesUnits(product.sales_units, isAverageProduct)}</Table.Cell>
            <Table.Cell>
              {product.is_average_product ? (
                <p>
                  {product.linkedSalesUnitsAverageClimateImpact
                    ? product.linkedSalesUnitsAverageClimateImpact.toFixed(2)
                    : "Calculation is in progress"}
                </p>
              ) : (
                product.sales_units?.map((unit: any) => (
                  <div className="salesUnitDataView">
                    <p key={unit._id}>
                      {checkIsUserSupplier && unit.climate_impact
                        ? "Calculated: Upgrade to see results"
                        : unit.climate_impact
                        ? unit.climate_impact.replace(/\s*kgCO2eq\s*$/, "")
                        : "Calculation is in progress"}
                    </p>
                  </div>
                )) || "-"
              )}
            </Table.Cell>
            <Table.Cell>
              {product.sales_units?.map((unit: any) => (
                <div className="salesUnitDataView">
                  <p key={unit._id}>{unit.ean_barcode || "-"}</p>
                </div>
              ))}
            </Table.Cell>
            <Table.Cell>
              <Grid>
                <Grid.Column computer={4} tablet={5} mobile={5}>
                  <TbFileSearch
                    cursor="pointer"
                    onClick={() => getProductDetails(product._id, modelContentType.view)}
                    size={24}
                    color="var(--tableEditIcon)"
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={5} mobile={5}>
                  <MdModeEditOutline
                    cursor="pointer"
                    onClick={() => getProductDetails(product._id, modelContentType.edit)}
                    size={24}
                    color="var(--tableEditIcon)"
                  />
                </Grid.Column>
                <Grid.Column computer={4} tablet={5} mobile={5}>
                  <MdDeleteOutline
                    cursor="pointer"
                    size={24}
                    color="var(--tableEditIcon)"
                    onClick={() => {
                      setVisibleDeleteModal(true);
                      setDeleteData(product);
                    }}
                  />
                </Grid.Column>
              </Grid>
            </Table.Cell>
          </Table.Row>
        );
      });
    }
  };

  if (isLoading) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  const renderSalesUnits = (salesUnits: any[], isAverageProduct: boolean) => {
    if (!salesUnits || salesUnits.length === 0) return "-";
    return salesUnits.map(unit => (
      <div className="salesUnitLink" key={unit._id}>
        {isAverageProduct ? (
          <span>{unit.sales_unit_name || "-"}</span>
        ) : (
          <Link to={`/mapping/${MappingSourceType.product}?sourceId=${unit._id}`}>
            {unit.sales_unit_name || "-"}
          </Link>
        )}
      </div>
    ));
  };

  return (
    <>
      <TitleBar enableTitle titleName="Products" />
      <Grid centered>
        <Grid.Column computer={4}>
          <InputText
            register={register}
            name="searchProduct"
            placeholder="Search Product"
            defaultValues={searchKey}
            onChangeFunction={(e: any) => {
              setSearchKey(e.target.value);
            }}
          />
        </Grid.Column>
        <Grid.Column computer={2}>
          <ProductFilter refetchWithFilters={refetchWithFilters} />
        </Grid.Column>
        <Grid.Column computer={10} textAlign="right">
          <CustomButton
            theme="green"
            title="Add New Product"
            onClick={() => setVisibleModal(true)}
            iconWithText={true}
            iconChild={<PlusIcon />}
          />
        </Grid.Column>
      </Grid>
      <Grid>
        <Grid.Column computer={16} tablet={16} mobile={16}>
          <CommonTable
            enableTableFooter
            tableHeaderData={ProductTableCol}
            singleLine={true}
            tableFooterChild={
              <PaginationView
                currentPageNo={pageNumber}
                totalPages={totalPages}
                pageSize={pageSize}
                currentSelectPage={setPageNumber}
                changePageSize={(size: any) => {
                  setPageSize(size);
                  setPageNumber(1);
                }}
              />
            }
          >
            {data?.result?.map((product, index) => {
              const isAverageProduct = product?.is_average_product || false;
              return (
                <Table.Row className="tbleR" key={index}>
                  <Table.Cell>
                    <p>{product.product_name}</p>
                  </Table.Cell>
                  <Table.Cell>{renderSalesUnits(product.sales_units, isAverageProduct)}</Table.Cell>
                  <Table.Cell>
                    {product.is_average_product ? (
                      <p>
                        {product.linkedSalesUnitsAverageClimateImpact
                          ? product.linkedSalesUnitsAverageClimateImpact.toFixed(2)
                          : "Data collection in progress"}
                      </p>
                    ) : (
                      product.sales_units?.map((unit: any) => (
                        <div className="salesUnitDataView">
                          <p key={unit._id}>
                            {checkIsUserSupplier && unit.climate_impact
                              ? "Calculated: Upgrade to see results"
                              : unit.climate_impact
                              ? unit.climate_impact.replace(/\s*kgCO2eq\s*$/, "")
                              : "Data collection in progress"}
                          </p>
                        </div>
                      )) || "-"
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {product.sales_units?.map((unit: any) => (
                      <div className="salesUnitDataView">
                        <p key={unit._id}>{unit.ean_barcode || "-"}</p>
                      </div>
                    ))}
                  </Table.Cell>
                  <Table.Cell>
                    <Grid>
                      <Grid.Column computer={4} tablet={5} mobile={5}>
                        <TbFileSearch
                          cursor="pointer"
                          onClick={() => getProductDetails(product._id, modelContentType.view)}
                          size={24}
                          color="var(--tableEditIcon)"
                        />
                      </Grid.Column>
                      <Grid.Column computer={4} tablet={5} mobile={5}>
                        <MdModeEditOutline
                          cursor="pointer"
                          onClick={() => getProductDetails(product._id, modelContentType.edit)}
                          size={24}
                          color="var(--tableEditIcon)"
                        />
                      </Grid.Column>
                      <Grid.Column computer={4} tablet={5} mobile={5}>
                        <MdDeleteOutline
                          cursor="pointer"
                          size={24}
                          color="var(--tableEditIcon)"
                          onClick={() => {
                            setVisibleDeleteModal(true);
                            setDeleteData(product);
                          }}
                        />
                      </Grid.Column>
                    </Grid>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </CommonTable>
        </Grid.Column>
      </Grid>
      <ConfirmModal
        viewModal={visibleDeleteModal}
        closeModal={() => setVisibleDeleteModal(false)}
        approve={handleProductDeleteConfirmation}
        title="Delete Product Data"
        subTitle="Are you sure you want to delete your product data?"
      />
      <ProductRegisterModal
        visibleModal={visibleModal}
        setVisibleModal={(status: any, data: any) => {
          handleNavigationRegister(status, data);
        }}
      />
      {visibleProductDetailsModal ? (
        <ProductDetailModal
          visibleModal={visibleProductDetailsModal}
          setVisibleModal={(status: any) => {
            setVisibleProductDetailsModal(false);
          }}
          productId={productId}
          openEditDetails={() => {
            setVisibleProductDetailsModal(false);
            setVisibleProductEditModal(true);
          }}
        />
      ) : null}
      {visibleProductEditModal ? (
        <ProductEditModal
          visibleModal={visibleProductEditModal}
          setVisibleModal={(status: any) => {
            if (status) {
              refetch();
              setVisibleProductEditModal(false);
            }
          }}
          productId={productId}
        />
      ) : null}
      {visibleRegModal ? (
        <IngredientRegisterModalView
          state={dataToPass}
          visibleModal={visibleRegModal}
          setVisibleModal={() => {
            setDataToPass({});
            setVisibleRegModal(false);
          }}
        />
      ) : null}
      <ConfirmModal
        viewModal={visibleDeleteModal}
        closeModal={() => setVisibleDeleteModal(false)}
        cancel={() => {
          setVisibleDeleteModal(false);
          setDeleteData({});
        }}
        approve={() => {
          setVisibleDeleteModal(false);
          handleProductDeleteConfirmation();
        }}
        title="Delete Product Data"
        subTitle="Are you sure you want to delete your product data?"
      />
    </>
  );
};

export default ProductList;
